import { sentryMetricsCount, sentryMetricsDistribution } from '@paragon/client-tools';

import { IMonitor, Metadata } from './types';
import { combineMetadata } from './util/combineMetadata';

export class Monitor implements IMonitor {
  metadata: Metadata = null;

  private lastTimerId = -1;
  private activeTimers = new Map<number, { name: string; start: number; metadata: Metadata }>();

  appendMetadata(metadata: Metadata): void {
    this.metadata = combineMetadata(this.metadata, metadata);
  }

  beginTimedOperation(name: string, metadata: Metadata = null): number {
    this.lastTimerId += 1;

    this.activeTimers.set(this.lastTimerId, { name, start: Date.now(), metadata });
    return this.lastTimerId;
  }

  concludeTimedOperation(id: number, metadata: Metadata = null): void {
    const timer = this.activeTimers.get(id);
    if (!timer) return;

    const timestamp = Date.now();
    const duration = timestamp - timer.start;

    const combinedMetadata = combineMetadata(this.metadata, timer.metadata, metadata, { timestamp });
    sentryMetricsDistribution(timer.name, duration, combinedMetadata ?? undefined);
  }

  recordInteraction(name: string, metadata: Metadata = null): void {
    const combinedMetadata = combineMetadata(this.metadata, metadata, { timestamp: Date.now() });
    sentryMetricsCount(name, combinedMetadata ?? undefined);
  }

  scope(metadata: Metadata = null): IMonitor {
    return new ScopedMonitor(this, metadata);
  }
}

class ScopedMonitor implements IMonitor {
  metadata: Metadata;

  private parent: Monitor;

  constructor(parent: Monitor, metadata: Metadata = null) {
    this.parent = parent;
    this.metadata = metadata;
  }

  private getCombinedMetadata(metadata: Metadata) {
    return combineMetadata(this.parent.metadata, this.metadata, metadata);
  }

  beginTimedOperation(name: string, metadata: Metadata = null): number {
    return this.parent.beginTimedOperation(name, this.getCombinedMetadata(metadata));
  }

  concludeTimedOperation(id: number, metadata: Metadata = null): void {
    this.parent.concludeTimedOperation(id, this.getCombinedMetadata(metadata));
  }

  recordInteraction(name: string, metadata: Metadata = null): void {
    this.parent.recordInteraction(name, this.getCombinedMetadata(metadata));
  }
}
